import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ElementRef, Input, Pipe, PipeTransform, Output, EventEmitter, ViewEncapsulation, AfterViewInit, OnChanges } from '@angular/core';
import { UntypedFormBuilder, FormGroup, Validators, ControlContainer, FormGroupDirective, FormControl, UntypedFormGroup } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { interval, Subscription, Observable, forkJoin, Subject, from, of } from 'rxjs';
import { debounceTime, map, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { ActivatedRoute, Router } from '@angular/router';
import { VendorService } from '../../services/vendor.service';
import { PurchasingService } from '../../services/purchasing.service';
import { GlobalsService } from '../../services/globals.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { trigger, state, style, transition, animate, query, group, stagger } from '@angular/animations';
import { Location } from '@angular/common';
import { NoPoItemsModalComponent } from './no-po-items-modal/no-po-items-modal.component';
import { GlPreviewModalComponent, GlPreviewModalData } from '../../shared/gl-preview-modal/gl-preview-modal.component';
import { DocumentIntakeService, VendorInvoiceFields } from '../../services/document-intake.service';

@Component({
	selector: 'app-vendor-invoice',
	templateUrl: './vendor-invoice.component.html',
	styleUrls: ['./vendor-invoice.component.scss']
})
export class VendorInvoiceComponent implements OnInit, OnDestroy, OnChanges {
	@Input() bankRecContext: any = null;

	invoiceForm: FormGroup;
	separatorKeysCodes: number[] = [COMMA]; // Removed ENTER - GRNs should only be selected via autocomplete
	vendor_data: any = [];
	open_grns: any = [];
	all_open_grns: any = [];
	selected_grns: any = [];
	selected_grnbatches: any = [];
	invoice_grns: any = [];
	invoiceTotal = 0.00;
	fetTotal = 0.00;
	taxTotal = 0.00;
	totalTotal = 0.00;
	glcharges: any = [];
	glaccounts: any = [];
	taxes: any = [];
	today = new Date();
	overide_tax = false;
	grn_batches: any = [];
	all_grn_batches: any = [];
	discount: any = 0.00;
	user: any = [];
	errors: any = [];
	color: any = '';
	sending = false;
	allglaccounts: any = [];
	grnValueChangesSubscription: Subscription | null = null;
	referenceCheck$ = new Subject<string>();
	referenceCheckSubscription: Subscription | null = null;
	resetGlLookup = 0;
	config: any = [];
	glTags: { tagId?: number; tagref?: number; description?: string }[] = [];
	currencies: any[] = [];
	@Input() current_supplier = false;
	getting_supplier: any = false;
	@ViewChild('grnInput') grnInput: ElementRef < HTMLInputElement > ;
	attachmentFiles: File[] = [];

	// AI invoice reader — pre-fills the header and suggests open GRNs; operator reviews & posts.
	extracting = false;
	extracted: VendorInvoiceFields | null = null;
	extractWarnings: string[] = [];
	suggestedBatches: any[] = [];
	extractedLineStatus: any[] = [];

	@Output() newvendortrans = new EventEmitter<boolean>();

	constructor(private route: ActivatedRoute, private fb: UntypedFormBuilder, private vendorService: VendorService, public router: Router, private location: Location, private globalsService: GlobalsService, private generalLedgerService: GeneralLedgerService, public purchasingService: PurchasingService, private globalSearchService: GlobalSearchService, private dialog: MatDialog, private documentIntakeService: DocumentIntakeService) {
		this.color = this.globalSearchService.getColor()
		this.globalSearchService.user.subscribe((user: any) => {
			this.user = user
		});

		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
		});
	}

	ngOnInit(): void {
		// Embedded callers provide current_supplier. ngOnChanges owns that load; do
		// not cancel it by attempting a second vendor request with no route id.
		if (this.current_supplier) { return; }
		this.route.params.subscribe(params => {
			if (params['id']) { this.buildInvoiceForm(params['id']); }
		});
	}


	buildInvoiceForm(supplierid){

		if(this.getting_supplier){
			this.getting_supplier.unsubscribe();
		}

		this.getting_supplier = this.vendorService.getVendor(supplierid).subscribe((results: any) => {
			this.getting_supplier = false;
			this.vendor_data = results;

			this.purchasingService.getGrnDetails(this.vendor_data.supplierid).subscribe((r: any) => {
				this.open_grns = r;
				this.all_open_grns = r;
			})

			this.purchasingService.getOpenGrnBatches(this.vendor_data.supplierid).subscribe((r: any) => {
				this.grn_batches = r;
				this.all_grn_batches = r;
			})
			const due = new Date(this.vendor_data.duedate);

			this.invoiceForm = this.fb.group({
				reference: ['', [Validators.required]],
				invoiceDate: [this.today, [Validators.required]],
				discountAmount: [0.00, [Validators.pattern('[0-9.]*')]],
				discountDate: [due, [Validators.required]],
				dueDate: [due, [Validators.required]],
				rate: [this.roundExchangeRate(this.vendor_data.exrate) ?? this.vendor_data.exrate, [Validators.pattern('[0-9.]*')]],
				currcode: [this.vendor_data.currcode || ''],
				grns: [''],
				glsearch: [''],
				tag: [null]
			});
			if (this.bankRecContext) {
				const bankDate = String(this.bankRecContext.postedYmd || this.bankRecContext.posted || '');
				const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(bankDate) ? bankDate + 'T00:00:00' : bankDate);
				this.invoiceForm.patchValue({
					reference: 'BANK-' + String(this.bankRecContext.sf_id || '').slice(-12),
					invoiceDate: isNaN(d.getTime()) ? this.today : d,
				});
			}

			// Auto-update due date and discount date when invoice date changes (computed by backend)
			this.invoiceForm.get('invoiceDate')?.valueChanges.pipe(
				debounceTime(200),
				switchMap((d: Date) => {
					if (!d) { return of(null); }
					return this.vendorService.computeDueDate({
						invoiceDate: d instanceof Date ? d.toISOString() : String(d),
						daysbeforedue: parseInt(this.vendor_data?.daysbeforedue) || 0,
						dayinfollowingmonth: parseInt(this.vendor_data?.dayinfollowingmonth) || 0,
						statementperiods: parseInt(this.vendor_data?.statementperiods) || 0,
					});
				})
			).subscribe((result: any) => {
				if (result?.duedate) {
					const newDue = new Date(result.duedate);
					this.invoiceForm.get('dueDate')?.setValue(newDue, { emitEvent: false });
					this.invoiceForm.get('discountDate')?.setValue(newDue, { emitEvent: false });
				}
			});

			// Auto-update exchange rate when user picks a different currency
			this.invoiceForm.get('currcode')?.valueChanges.subscribe((code: string) => {
				if (code) {
					this.invoiceForm.get('rate')?.setValue(this.rateForCurrcode(code), { emitEvent: false });
				}
			});

			if (this.grnValueChangesSubscription) {
				this.grnValueChangesSubscription.unsubscribe();
			}
			this.grnValueChangesSubscription = this.invoiceForm.get('grns').valueChanges.subscribe(newValue => {

				if (newValue != '') {
					this.grn_batches = this.globalSearchService.filterItem(this.all_grn_batches, newValue, 'grnbatch,grnno,orderno,itemcode,sales_orderno')
				} else {
					this.grn_batches = this.all_grn_batches
				}

			});

			if (this.referenceCheckSubscription) {
				this.referenceCheckSubscription.unsubscribe();
			}
			this.referenceCheckSubscription = this.referenceCheck$.pipe(
				debounceTime(400),
				switchMap(ref => this.vendorService.checkInvoiceReference(this.vendor_data.supplierid, ref))
			).subscribe((res: any) => {
				if (res && res.used) {
					this.invoiceForm.get('reference')?.setErrors({ referenceUsed: true });
					this.globalSearchService.showNotification('Invoice reference already used for this vendor.', 'danger', 'bottom', 'right');
				} else {
					const refControl = this.invoiceForm.get('reference');
					const errs = refControl?.errors;
					if (errs?.referenceUsed) {
						const next = { ...errs };
						delete next.referenceUsed;
						refControl.setErrors(Object.keys(next).length ? next : null);
					}
				}
			});

			this.invoiceForm.markAllAsTouched();

			// If currencies were already loaded before this vendor fetch completed, sync now.
			this.syncCurrencyToForm();
		});
	}

	ngOnDestroy(): void {
		if (this.grnValueChangesSubscription) {
			this.grnValueChangesSubscription.unsubscribe();
		}
		if (this.referenceCheckSubscription) {
			this.referenceCheckSubscription.unsubscribe();
		}
	}

	ngOnChanges(changes): void {
		if (changes.current_supplier && changes.current_supplier.currentValue && changes.current_supplier.currentValue !== changes.current_supplier.previousValue) {
			this.selected_grnbatches = [];
			this.selected_grns = [];
			this.grn_batches = [];
			this.buildInvoiceForm(changes.current_supplier.currentValue);
			// vendor_data will be set inside buildInvoiceForm; sync happens via currencies subscription
		}
	}

	back(): void {
		this.location.back()
	}

	onAttachmentSelect(event: { addedFiles: File[] }) {
		this.attachmentFiles.push(...event.addedFiles);
	}

	onAttachmentRemove(file: File) {
		this.attachmentFiles = this.attachmentFiles.filter(f => f !== file);
	}

	/** Operator dropped a vendor-invoice PDF — read it with Claude and pre-fill the form. */
	onInvoicePdfSelected(event: { addedFiles: File[] }) {
		const file = event && event.addedFiles && event.addedFiles.length ? event.addedFiles[0] : null;
		if (!file || this.extracting) { return; }
		this.extracting = true;
		this.documentIntakeService.fileToBase64(file).then(b64 => {
			this.documentIntakeService.extractVendorInvoice(b64).subscribe((res: any) => {
				this.extracting = false;
				if (!res || !res.success || !res.invoice) {
					this.globalSearchService.showNotification(res?.message || 'Could not read this invoice PDF.', 'warning', 'bottom', 'right');
					return;
				}
				this.applyExtractedInvoice(res.invoice);
				// Also keep the PDF on the invoice as an attachment so the source doc is stored.
				if (!this.attachmentFiles.includes(file)) { this.attachmentFiles.push(file); }
			}, () => {
				this.extracting = false;
				this.globalSearchService.showNotification('Invoice read failed — enter the invoice manually.', 'danger', 'bottom', 'right');
			});
		}).catch(() => {
			this.extracting = false;
			this.globalSearchService.showNotification('Could not read that file.', 'danger', 'bottom', 'right');
		});
	}

	/** Pre-fill the header from the extraction and build GRN suggestions. Pre-fill only — nothing posts. */
	private applyExtractedInvoice(inv: VendorInvoiceFields): void {
		this.extracted = inv;
		this.extractWarnings = [];

		if (inv.document_type === 'credit_memo') {
			this.extractWarnings.push('This looks like a credit memo, not a purchase invoice — review before posting.');
		}
		// Sanity check the extracted vendor against the supplier this screen is for.
		const got = this.normCode(inv.vendor_name);
		const cur = this.normCode(this.vendor_data?.suppname);
		if (got && cur && got !== cur && cur.indexOf(got) < 0 && got.indexOf(cur) < 0) {
			this.extractWarnings.push('Invoice vendor "' + inv.vendor_name + '" does not match this supplier (' + (this.vendor_data?.suppname || '') + ').');
		}

		if (inv.invoice_number) {
			this.invoiceForm.get('reference')?.setValue(inv.invoice_number);
			this.onReferenceBlur();
		}
		// Set the invoice date WITHOUT emitting, so the auto due-date compute doesn't clobber an
		// extracted due/discount date (same one-shot guard the credit-memo intake needed).
		if (inv.invoice_date) {
			const d = new Date(inv.invoice_date + 'T00:00:00');
			if (!isNaN(d.getTime())) { this.invoiceForm.get('invoiceDate')?.setValue(d, { emitEvent: false }); }
		}
		const applyDue = (iso: string) => {
			const d = new Date(iso + 'T00:00:00');
			if (isNaN(d.getTime())) { return; }
			this.invoiceForm.get('dueDate')?.setValue(d, { emitEvent: false });
			if (!inv.discount_date) { this.invoiceForm.get('discountDate')?.setValue(d, { emitEvent: false }); }
		};
		if (inv.due_date) {
			applyDue(inv.due_date);
		} else if (inv.invoice_date) {
			this.vendorService.computeDueDate({
				invoiceDate: inv.invoice_date,
				daysbeforedue: parseInt(this.vendor_data?.daysbeforedue) || 0,
				dayinfollowingmonth: parseInt(this.vendor_data?.dayinfollowingmonth) || 0,
				statementperiods: parseInt(this.vendor_data?.statementperiods) || 0,
			}).subscribe((r: any) => { if (r?.duedate) { applyDue(r.duedate); } });
		}
		if (inv.discount_date) {
			const d = new Date(inv.discount_date + 'T00:00:00');
			if (!isNaN(d.getTime())) { this.invoiceForm.get('discountDate')?.setValue(d, { emitEvent: false }); }
		}
		if (inv.discount_amount > 0) {
			this.invoiceForm.get('discountAmount')?.setValue(inv.discount_amount);
			this.discount = inv.discount_amount;
		}

		this.buildGrnSuggestions();
		this.getTotal();
	}

	/** Match each extracted line to the vendor's open GRNs (by item code) → suggested batches + per-line status. */
	private buildGrnSuggestions(): void {
		this.suggestedBatches = [];
		this.extractedLineStatus = [];
		const lines = (this.extracted && this.extracted.lines) ? this.extracted.lines : [];
		const open = (this.all_open_grns && this.all_open_grns.length) ? this.all_open_grns : this.open_grns;

		const extCodes: any = {};
		lines.forEach((l: any) => { const c = this.normCode(l.itemcode); if (c) { extCodes[c] = true; } });

		const matchedCodes: any = {};
		const batchMap: any = {};   // key "grnbatch|orderno" -> { batch, items{} }
		(open || []).forEach((g: any) => {
			const code = this.normCode(g.itemcode);
			if (!code || !extCodes[code]) { return; }
			matchedCodes[code] = true;
			const key = (parseFloat(g.grnbatch) || 0) + '|' + (parseFloat(g.orderno) || 0);
			if (!batchMap[key]) {
				let b = (this.all_grn_batches || []).find((bb: any) =>
					(parseFloat(bb.grnbatch) || 0) == (parseFloat(g.grnbatch) || 0) &&
					(parseFloat(bb.orderno) || 0) == (parseFloat(g.orderno) || 0));
				if (!b) { b = { grnbatch: g.grnbatch, orderno: g.orderno, sales_orderno: g.sales_orderno }; }
				batchMap[key] = { batch: b, items: {} };
			}
			batchMap[key].items[g.itemcode] = true;
		});

		Object.keys(batchMap).forEach(key => {
			const entry = batchMap[key];
			const already = this.selected_grnbatches.some((sb: any) =>
				(parseFloat(sb.grnbatch) || 0) == (parseFloat(entry.batch.grnbatch) || 0) &&
				(parseFloat(sb.orderno) || 0) == (parseFloat(entry.batch.orderno) || 0));
			if (already) { return; }
			const items = Object.keys(entry.items);
			this.suggestedBatches.push({ ...entry.batch, matchedItems: items, matchCount: items.length });
		});

		this.extractedLineStatus = lines.map((l: any) => {
			const c = this.normCode(l.itemcode);
			return {
				itemcode: l.itemcode, description: l.description, qty: l.qty,
				unit_price: l.unit_price, extended: l.extended,
				matched: !!c && !!matchedCodes[c],
			};
		});
	}

	/** Operator confirmed a suggested batch — add it (same path as the picker) and pull invoice prices onto matched lines. */
	addSuggestedBatch(batch: any): void {
		this.addGrnBatch(batch);
		this.applyExtractedPricesToSelected();
		this.suggestedBatches = this.suggestedBatches.filter(b =>
			!((parseFloat(b.grnbatch) || 0) == (parseFloat(batch.grnbatch) || 0) &&
			  (parseFloat(b.orderno) || 0) == (parseFloat(batch.orderno) || 0)));
		this.getTotal();
	}

	addAllSuggestedBatches(): void {
		const batches = [...this.suggestedBatches];
		batches.forEach(b => this.addGrnBatch(b));
		this.applyExtractedPricesToSelected();
		this.suggestedBatches = [];
		this.getTotal();
	}

	/** Set the invoice unit price on matched selected lines from the extracted invoice (operator can still edit). */
	private applyExtractedPricesToSelected(): void {
		const lines = (this.extracted && this.extracted.lines) ? this.extracted.lines : [];
		if (!lines.length) { return; }
		const byCode: any = {};
		lines.forEach((l: any) => { const c = this.normCode(l.itemcode); if (c && l.unit_price > 0) { byCode[c] = l.unit_price; } });
		this.selected_grns.forEach((g: any) => {
			const c = this.normCode(g.itemcode);
			if (byCode[c] != null && !g.invoicePriceApplied) {
				g.unitprice = byCode[c];
				g.invoicePriceApplied = true;
			}
		});
	}

	/** True when the entered invoice total reconciles to the extracted invoice total (to the cent). */
	extractedTotalMatches(): boolean {
		if (!this.extracted) { return false; }
		return Math.abs((Number(this.extracted.total_amount) || 0) - (Number(this.totalTotal) || 0)) <= 0.01;
	}

	/** Item code normalized for matching: upper-case, alphanumerics only. */
	private normCode(s: any): string {
		return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	private readFileAsDataUrl(file: File): Promise<string | ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve((e.target as FileReader).result);
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	navigateToPO(orderno: string) {
		if (orderno && this.vendor_data && this.vendor_data.supplierid) {
			this.router.navigate(['/purchasing', this.vendor_data.supplierid, orderno]);
		}
	}

	add(event: MatChipInputEvent): void {
		// Prevent manual entry - GRNs should only be added via autocomplete selection
		// This prevents adding undefined/invalid entries when Enter is pressed
		event.chipInput!.clear();
		this.grnInput.nativeElement.value = '';
		this.invoiceForm.controls['grns'].setValue(null);
	}

	removeLine(grn: any): void {
		const index = this.selected_grns.indexOf(grn);

		if (index >= 0) {
			this.selected_grns.splice(index, 1);
		}
		this.getTotal();
	}

	remove(grn: any): void {
		// Find and remove the batch from selected_grnbatches
		const batchIndex = this.selected_grnbatches.indexOf(grn);

		if (batchIndex >= 0) {
			// Add the batch back to the available list
			this.grn_batches.push(this.selected_grnbatches[batchIndex]);
			// Remove the batch from selected batches
			this.selected_grnbatches.splice(batchIndex, 1);
		}

		// Remove all GRN items that belong to this specific batch
		// Match by both grnbatch and orderno to ensure accuracy (same logic as selected method)
		const batchGrnbatch = parseFloat(grn.grnbatch) || 0;
		const batchOrderno = parseFloat(grn.orderno) || 0;

		const selected = this.selected_grns.filter((line: any) => {
			const lineGrnbatch = parseFloat(line.grnbatch) || 0;
			const lineOrderno = parseFloat(line.orderno) || 0;
			// Keep items that don't match both grnbatch AND orderno
			return !(lineGrnbatch == batchGrnbatch && lineOrderno == batchOrderno);
		});

		this.selected_grns = selected;

		this.getTotal();
	}


	selected(event: MatAutocompleteSelectedEvent): void {
		this.addGrnBatch(event.option.value);
		this.grnInput.nativeElement.value = '';
		this.invoiceForm.controls['grns'].setValue(null);
	}

	/**
	 * Add a GRN batch and all its lines to the invoice. Shared by the autocomplete picker
	 * (selected) and the AI "Suggested GRNs" Add buttons. Guards against adding the same batch
	 * twice so a suggestion the operator already picked manually is a no-op.
	 */
	addGrnBatch(selectedBatch: any): void {
		if (!selectedBatch) { return; }
		const batchGrnbatch = selectedBatch.grnbatch;
		const batchOrderno = selectedBatch.orderno;

		const alreadyBatch = this.selected_grnbatches.some((b: any) =>
			parseFloat(b.grnbatch) == parseFloat(batchGrnbatch) && parseFloat(b.orderno) == parseFloat(batchOrderno));
		if (alreadyBatch) { return; }

		const index = this.grn_batches.indexOf(selectedBatch);
		//add to chip list
		this.selected_grnbatches.push(selectedBatch)

		if (index >= 0) { this.grn_batches.splice(index, 1); }

		// Filter and add all GRN items that belong to this specific batch
		// Match by both grnbatch and orderno to ensure accuracy
		// Also check if the GRN item is already selected to prevent duplicates
		let itemsAdded = false;
		this.open_grns.forEach((grn: any) => {
			if (parseFloat(grn.grnbatch) == parseFloat(batchGrnbatch) &&
			    parseFloat(grn.orderno) == parseFloat(batchOrderno)) {
				// Check if this GRN item is already in selected_grns to prevent duplicates
				const alreadySelected = this.selected_grns.some((selectedGrn: any) =>
					parseFloat(selectedGrn.grnno) == parseFloat(grn.grnno)
				);
				if (!alreadySelected) {
					// Ensure toinvoice field is set (should come from server, but set default if missing)
					if (grn.toinvoice === undefined || grn.toinvoice === null) {
						grn.toinvoice = (parseFloat(grn.qtyrecd) || 0) - (parseFloat(grn.quantityinv) || 0);
					}
					// Ensure toinvoice is a number, not a string
					grn.toinvoice = parseFloat(grn.toinvoice) || 0;
					//add line notes field
					grn.additionaldesc = '';
					this.snapshotPoUnitPrice(grn);
					this.selected_grns.push(grn);
					itemsAdded = true;
				}
			}
		});

		// If no items were found in open_grns, try searching in all_open_grns
		if (!itemsAdded && this.all_open_grns && this.all_open_grns.length > 0) {
			this.all_open_grns.forEach((grn: any) => {
				if (parseFloat(grn.grnbatch) == parseFloat(batchGrnbatch) &&
				    parseFloat(grn.orderno) == parseFloat(batchOrderno)) {
					const alreadySelected = this.selected_grns.some((selectedGrn: any) =>
						parseFloat(selectedGrn.grnno) == parseFloat(grn.grnno)
					);
					if (!alreadySelected) {
						// Ensure toinvoice field is set
						if (grn.toinvoice === undefined || grn.toinvoice === null) {
							grn.toinvoice = (parseFloat(grn.qtyrecd) || 0) - (parseFloat(grn.quantityinv) || 0);
						}
						grn.toinvoice = parseFloat(grn.toinvoice) || 0;
						this.snapshotPoUnitPrice(grn);
						this.selected_grns.push(grn);
					}
				}
			});
		}

		this.getTotal();
	}

	removeGl(gl: string): void {
		const index = this.glcharges.indexOf(gl);

		if (index >= 0) {
			this.glcharges.splice(index, 1);
		}
		this.getTotal();
	}

	setTaxValue(event: any, tax: any) {
		this.overide_tax = true;
		const index = this.vendor_data.taxes.indexOf(tax);
		this.vendor_data.taxes[index].taxamount = event.target.value;
		this.getTotal();
	}

	/** PO line unit price from API (`purchorderdetails.unitprice`); invoice edits `unitprice` separately for posting. */
	private snapshotPoUnitPrice(grn: any): void {
		const p = parseFloat(grn.unitprice);
		grn.poUnitPrice = Number.isFinite(p) ? p : 0;
	}

	invoicePriceDiffersFromPo(grn: any): boolean {
		const po = parseFloat(grn.poUnitPrice);
		const inv = parseFloat(grn.unitprice);
		if (!Number.isFinite(po) || !Number.isFinite(inv)) {
			return false;
		}
		return Math.abs(po - inv) > 0.000001;
	}

	/** True when invoice and PO unit prices agree (for matched-state styling after user corrects price). */
	invoicePriceMatchesPo(grn: any): boolean {
		const po = parseFloat(grn.poUnitPrice);
		const inv = parseFloat(grn.unitprice);
		if (!Number.isFinite(po) || !Number.isFinite(inv)) {
			return false;
		}
		return Math.abs(po - inv) <= 0.000001;
	}

	invoicePricePoDiffTooltip(grn: any): string {
		if (!this.invoicePriceDiffersFromPo(grn)) {
			return '';
		}
		const po = Number(grn.poUnitPrice);
		return 'Invoice price differs from PO line price (' + (Number.isFinite(po) ? po.toFixed(2) : '?') + ')';
	}

	invoicePriceMatchTooltip(grn: any): string {
		if (!this.invoicePriceMatchesPo(grn)) {
			return '';
		}
		const po = Number(grn.poUnitPrice);
		return 'Matches PO unit price (' + (Number.isFinite(po) ? po.toFixed(2) : '?') + ')';
	}

	/** Pill styling aligned with order item lookup QOH pills (beauty-wqoh compact-pill). */
	vendorRecvdOrdPillClass(grn: any): string {
		const recd = parseFloat(grn.qtyrecd) || 0;
		const ord = parseFloat(grn.quantityord) || 0;
		if (recd !== ord) {
			return 'vendor-qty-pill vendor-qty-pill--red';
		}
		return 'vendor-qty-pill vendor-qty-pill--green';
	}

	vendorInvcdRecdPillClass(grn: any): string {
		const inv = parseFloat(grn.quantityinv) || 0;
		const recd = parseFloat(grn.qtyrecd) || 0;
		if (inv > recd) {
			return 'vendor-qty-pill vendor-qty-pill--red';
		}
		if (recd > 0 && inv < recd) {
			return 'vendor-qty-pill vendor-qty-pill--yellow';
		}
		return 'vendor-qty-pill vendor-qty-pill--green';
	}

	/** KM (`env.client`): allow invoice qty above uninvoiced received so PQV posts when supplier over-bills. */
	private allowVendorQtyOverReceived(): boolean {
		return this.config && this.config.env && this.config.env.client === 'km';
	}

	setQty(event: any, grn: any) {
		const index = this.selected_grns.indexOf(grn);
		const row = this.selected_grns[index];
		if (!this.allowVendorQtyOverReceived()) {
			const qtyrecd = parseFloat(row.qtyrecd) || 0;
			const quantityinv = parseFloat(row.quantityinv) || 0;
			const remaining = Math.max(0, qtyrecd - quantityinv);
			const maxQty = remaining > 0 ? remaining : qtyrecd;
			if (parseFloat(event.target.value) > maxQty) {
				event.target.value = maxQty;
			}
		}

		this.selected_grns[index].toinvoice = event.target.value;
		this.getTotal();
	}

	setDiscount(event: any) {
		// Clearing the box yields NaN, which poisons totalTotal → the invoice total reaches
		// the API as null and the G/L legs balance against 0.
		const amount = parseFloat(event.target.value);
		this.discount = isNaN(amount) ? 0 : amount;
		this.getTotal();
	}

	canDistributeDiscount(): boolean {
		return this.discount > 0 && this.selected_grns.length > 0;
	}

	/** Spread the header discount evenly per unit across the invoiced lines, then zero the
	 *  header box. Some vendors (BFG, Michelin, Uniroyal) bill list price plus a flat per-tire
	 *  rebate called out separately on the invoice; entering that as a lump discount here makes
	 *  the invoice total match but leaves each line's unit price at the pre-rebate (too high)
	 *  amount, which both trips the PO-price discrepancy check and inflates landed unit cost.
	 *  Folding it into the line price keeps the same total while fixing both. */
	distributeDiscountToLines(): void {
		const totalQty = this.selected_grns.reduce((sum, g) => sum + (parseFloat(g.toinvoice) || 0), 0);
		if (!totalQty || !this.discount) { return; }
		const perUnit = this.discount / totalQty;
		this.selected_grns.forEach(g => {
			const price = parseFloat(g.unitprice) || 0;
			g.unitprice = (price - perUnit).toFixed(2);
		});
		this.discount = 0;
		this.invoiceForm.get('discountAmount')?.setValue(0);
		this.getTotal();
	}

	setPrice(event: any, grn: any) {
		const index = this.selected_grns.indexOf(grn);
		this.selected_grns[index].unitprice = event.target.value;
		this.getTotal();
	}

	setNote(event: any, grn:any){
		const index = this.selected_grns.indexOf(grn);
		this.selected_grns[index].additionaldesc = event.target.value;
	}

	setGlPrice(event: any, gl: any) {
		const index = this.glcharges.indexOf(gl);
		this.glcharges[index].value = event.target.value;
		this.getTotal();
	}

	setGlNote(event: any, gl:any){
		const index = this.glcharges.indexOf(gl);
		this.glcharges[index].description = event.target.value;
	}

	// Per-line department (GL tag) override; null means "use the header default".
	setGlDept(event: any, gl: any) {
		const index = this.glcharges.indexOf(gl);
		this.glcharges[index].tag = event.value ?? null;
	}

	setGrnDept(event: any, grn: any) {
		const index = this.selected_grns.indexOf(grn);
		this.selected_grns[index].tag = event.value ?? null;
	}

	getSubTotal() {
		let itemtotal = 0
		itemtotal = this.selected_grns.reduce(function(accumulator, items) {
			return parseFloat(accumulator) + parseFloat(items.unitprice) * parseFloat(items.toinvoice);
		}, 0);

		let gltotal = 0
		gltotal = this.glcharges.reduce(function(accumulator, items) {
			return parseFloat(accumulator) + parseFloat(items.value);
		}, 0);

		// Calculate FET separately (not included in subtotal for tax purposes)
		// Only calculate FET if package is 'tires'
		let fetTotal = 0
		if (this.config && this.config.env && this.config.env.package == 'tires') {
			fetTotal = this.selected_grns.reduce(function(accumulator, items) {
				const fetcharge = parseFloat(items.fetcharge) || 0;
				return parseFloat(accumulator) + fetcharge * parseFloat(items.toinvoice);
			}, 0);
		}

		this.invoiceTotal = itemtotal + gltotal;
		this.fetTotal = fetTotal;
		return itemtotal + gltotal; // Return subtotal without FET

	}

	getTaxTotal(subtotal: any) {

		if (!this.overide_tax) {
			this.vendor_data.taxes.forEach((tax, index) => {
				const value = parseFloat(tax.taxrate) * parseFloat(subtotal)
				this.vendor_data.taxes[index].taxamount = value;
			});
		}

		let taxtotal = 0
		taxtotal = this.vendor_data.taxes.reduce(function(accumulator, tax) {
			return (parseFloat(accumulator) + parseFloat(tax.taxamount));
		}, 0);

		this.taxTotal = taxtotal
		return taxtotal;
	}

	get applicableTaxes(): any[] {
		if (!this.vendor_data?.taxes) { return []; }
		return this.vendor_data.taxes.filter((t: any) => parseFloat(t.taxrate) > 0);
	}

	private roundExchangeRate(value: number | string | null | undefined): number | null {
		const n = value != null ? Number(value) : NaN;
		return !isNaN(n) ? parseFloat(n.toFixed(4)) : null;
	}

	get rateDisplay(): string {
		const rate = this.invoiceForm.get('rate')?.value;
		return ((this.roundExchangeRate(rate) ?? 0) * 100).toFixed(4);
	}

	onRateInput(event: Event): void {
		const input = (event.target as HTMLInputElement).value;
		const numericValue = parseFloat(input) / 100;
		const rounded = this.roundExchangeRate(numericValue);
		if (rounded != null) { this.invoiceForm.get('rate')?.setValue(rounded); }
	}

	getTotal() {

		const subtotal = this.getSubTotal();
		const taxtotal = this.getTaxTotal(subtotal);
		// FET is added after taxes (can't tax tax)
		this.totalTotal = subtotal + taxtotal + this.fetTotal - this.discount;
	}

	filteredGrnBatches(): any[] {
	  if (!this.selected_grnbatches || this.selected_grnbatches.length === 0) {
			return this.grn_batches; // Return all if nothing is selected
	  }

	  // Filter out batches that are already selected
	  // Check by both grnbatch and orderno to ensure accuracy
	  return this.grn_batches.filter(batch => {
			return !this.selected_grnbatches.some(selectedBatch =>
				parseFloat(selectedBatch.grnbatch) == parseFloat(batch.grnbatch) &&
				parseFloat(selectedBatch.orderno) == parseFloat(batch.orderno)
			);
	  });
	}


	addGl(event: any) {
		const selection = event.option.value
		const useBankValues = !!this.bankRecContext && this.glcharges.length === 0 && this.selected_grns.length === 0;
		//{accountcode: "1", accountname: "Default Account", group: "Sales"}
		const newGlCharge = {
			account: selection.accountcode,
			name: selection.accountname,
			value: useBankValues ? Math.abs(Number(this.bankRecContext.amount) || 0) : 0.00,
			quantity: 1,
			group: selection.group,
			description: useBankValues ? String(this.bankRecContext.description || '') : '',
			tag: null,
		};

		this.glcharges.push(newGlCharge);
		this.getTotal();
		//this.glcharge.nativeElement.value = '';

	}

	onReferenceBlur() {
		const ref = this.invoiceForm?.get('reference')?.value?.trim();
		if (!ref || !this.vendor_data?.supplierid) {
			return;
		}
		const refControl = this.invoiceForm.get('reference');
		const errs = refControl?.errors;
		if (errs?.referenceUsed) {
			const next = { ...errs };
			delete next.referenceUsed;
			refControl.setErrors(Object.keys(next).length ? next : null);
		}
		this.referenceCheck$.next(ref);
	}

	showNoteField(grn){
		if(!grn.showadditional){
			grn.showadditional = true;
		}else{
			grn.showadditional = false;
			grn.additionaldesc = '';
		}
	}

	/** GL tag for API: mat-select may store `{ tagId, description }`; gltrans.tag is tinyint tagref. */
	private buildHeaderWithGlTag(): Record<string, unknown> {
		const raw = this.invoiceForm.value as Record<string, unknown>;
		const out = { ...raw };
		const tagRaw = out['tag'];
		let tagNum: number | null = null;
		if (tagRaw != null && tagRaw !== '') {
			if (typeof tagRaw === 'object' && tagRaw !== null && 'tagId' in tagRaw) {
				const id = Number((tagRaw as { tagId?: unknown }).tagId);
				if (!Number.isNaN(id) && id > 0) {
					tagNum = id;
				}
			} else if (typeof tagRaw === 'number' && tagRaw > 0) {
				tagNum = tagRaw;
			} else if (typeof tagRaw === 'string' && /^\d+$/.test(tagRaw)) {
				const id = parseInt(tagRaw, 10);
				if (id > 0) {
					tagNum = id;
				}
			}
		}
		out['tag'] = tagNum;
		return out;
	}

	onSubmit() {
		this.getTotal();
		this.errors = false;
		for (const el in this.invoiceForm.controls) {
			if (this.invoiceForm.controls[el].errors) {
				this.errors = true;
			}
		}

		if (this.invoiceForm.valid) {
			this.sending = true;
			const data = {
				vendor: this.vendor_data,
				header: this.buildHeaderWithGlTag(),
				grns: this.selected_grns,
				glcharges: this.glcharges,
				subtotal: this.invoiceTotal,
				taxes: this.taxTotal,
				fet: this.fetTotal,
				total: this.totalTotal,
				user: this.user,
			}

			this.vendorService.createInvoice(data).subscribe((result: any) => {
				this.sending = false;
				if (result.success) {
					this.globalSearchService.showNotification('#' + result.transno + ' Created', 'success', 'bottom', 'right');

					const transId = result.id;
					if (transId && this.attachmentFiles.length > 0) {
						const uploads = this.attachmentFiles.map((file: File) =>
							from(this.readFileAsDataUrl(file)).pipe(
								switchMap((content: string) => this.vendorService.uploadVendorAttachment(transId, {
									content,
									name: file.name,
									mime: file.type || 'application/octet-stream'
								}))
							)
						);
						forkJoin(uploads).subscribe({
							next: () => {
								this.globalSearchService.showNotification('Attachments uploaded', 'success', 'bottom', 'right');
								this.attachmentFiles = [];
							},
							error: () => {
								this.globalSearchService.showNotification('Some attachments failed to upload', 'warning', 'bottom', 'right');
								this.attachmentFiles = [];
							}
						});
					} else {
						this.attachmentFiles = [];
					}

					const due = new Date(this.vendor_data.duedate);
					this.invoiceForm.reset();

					// Unsubscribe from previous valueChanges subscription to prevent memory leaks
					if (this.grnValueChangesSubscription) {
						this.grnValueChangesSubscription.unsubscribe();
					}

					this.invoiceForm = this.fb.group({
						reference: ['', [Validators.required]],
						invoiceDate: [this.today, [Validators.required]],
						discountAmount: [0.00, [Validators.pattern('[0-9.]*')]],
						discountDate: [due, [Validators.required]],
						dueDate: [due, [Validators.required]],
						rate: [this.roundExchangeRate(this.vendor_data.exrate) ?? this.vendor_data.exrate, [Validators.pattern('[0-9.]*')]],
						currcode: [this.vendor_data.currcode || ''],
						grns: [''],
						glsearch: [''],
						tag: [null]
					});

					// Re-attach invoice date → due date reactive sync (computed by backend)
					this.invoiceForm.get('invoiceDate')?.valueChanges.pipe(
						debounceTime(200),
						switchMap((d: Date) => {
							if (!d) { return of(null); }
							return this.vendorService.computeDueDate({
								invoiceDate: d instanceof Date ? d.toISOString() : String(d),
								daysbeforedue: parseInt(this.vendor_data?.daysbeforedue) || 0,
								dayinfollowingmonth: parseInt(this.vendor_data?.dayinfollowingmonth) || 0,
								statementperiods: parseInt(this.vendor_data?.statementperiods) || 0,
							});
						})
					).subscribe((result: any) => {
						if (result?.duedate) {
							const newDue = new Date(result.duedate);
							this.invoiceForm.get('dueDate')?.setValue(newDue, { emitEvent: false });
							this.invoiceForm.get('discountDate')?.setValue(newDue, { emitEvent: false });
						}
					});

					this.glcharges = [];
					this.selected_grns = [];
					this.selected_grnbatches = [];

					this.totalTotal = 0;
					this.taxTotal = 0;
					this.discount = 0;

					this.resetGlLookup = (this.resetGlLookup || 0) + 1;

					// Reload GRN data after invoice creation to reflect updated quantities
					// Use forkJoin to ensure both calls complete before proceeding
					forkJoin({
						grnDetails: this.purchasingService.getGrnDetails(this.vendor_data.supplierid),
						grnBatches: this.purchasingService.getOpenGrnBatches(this.vendor_data.supplierid)
					}).subscribe((results: any) => {
						this.all_open_grns = results.grnDetails || [];
						this.all_grn_batches = results.grnBatches || [];
						// Update open_grns based on current form value
						const currentValue = this.invoiceForm.get('grns')?.value;
						if (currentValue && currentValue != '') {
							this.open_grns = this.globalSearchService.filterItem(this.all_open_grns, currentValue, 'grnbatch,grnno,orderno,itemcode,sales_orderno');
						} else {
							this.open_grns = this.all_open_grns;
						}
						// Update grn_batches based on current form value
						if (currentValue && currentValue != '') {
							this.grn_batches = this.globalSearchService.filterItem(this.all_grn_batches, currentValue, 'grnbatch,grnno,orderno,itemcode,sales_orderno');
						} else {
							this.grn_batches = this.all_grn_batches;
						}
					});

					// Set up new valueChanges subscription
					this.grnValueChangesSubscription = this.invoiceForm.get('grns').valueChanges.subscribe(newValue => {
						if (newValue != '') {
							this.grn_batches = this.globalSearchService.filterItem(this.all_grn_batches, newValue, 'grnbatch,grnno,orderno,itemcode,sales_orderno')
							this.open_grns = this.globalSearchService.filterItem(this.all_open_grns, newValue, 'grnbatch,grnno,orderno,itemcode,sales_orderno')
						} else {
							this.grn_batches = this.all_grn_batches
							this.open_grns = this.all_open_grns
						}
					});

					this.newvendortrans.emit(this.vendor_data.supplierid);

					this.invoiceForm.markAllAsTouched();
					this.syncCurrencyToForm();

				} else {
					this.globalSearchService.showNotification(result.message || 'Invoice could not be created.', 'danger', 'bottom', 'right');
				}
				//this.router.navigate(['/vendors/invoice/complete/'+result.transno]);
			});
		} else {
			this.globalSearchService.getFormValidationErrors(this.invoiceForm);
		}
	}

	openGlPreview(): void {
		if (!this.selected_grns.length && !this.glcharges.length) {
			this.globalSearchService.showNotification('Add GRN lines or GL charges to preview posting.', 'info', 'bottom', 'right');
			return;
		}
		this.getTotal();
		const payload = {
			vendor: this.vendor_data,
			header: this.buildHeaderWithGlTag(),
			grns: this.selected_grns,
			glcharges: this.glcharges,
			subtotal: this.invoiceTotal,
			taxes: this.taxTotal,
			fet: this.fetTotal,
			total: this.totalTotal,
			user: this.user,
		};
		this.dialog.open(GlPreviewModalComponent, {
			width: '1160px',
			maxWidth: '98vw',
			panelClass: 'gl-preview-dialog-panel',
			data: {
				title: 'Vendor invoice — GL preview',
				getPreview: (_excludeTypenos?: number[], _sampleScope?: 'all' | 'current_year') =>
					this.vendorService.previewInvoiceGl(payload),
			} as GlPreviewModalData,
		});
	}

	openNoPoItemsModal() {
		const dialogRef = this.dialog.open(NoPoItemsModalComponent, {
			width: '90%',
			maxWidth: '1400px',
			data: { supplierid: this.vendor_data.supplierid }
		});

		// Capture what the no-PO action just created and the GRN pool as it stood BEFORE the reload, so
		// we can ADD only the new lines and never disturb GRN lines already selected on this invoice.
		const priorGrnNos = new Set<string>((this.all_open_grns || []).map((g: any) => String(g.grnno)));
		dialogRef.afterClosed().subscribe(result => {
			if (!result || !result.success) { return; }
			const createdPoNos = new Set<string>((result.createdPOs || []).map((p: any) => String(p?.orderno ?? p ?? '')));
			const createdGrnNos = new Set<string>((result.createdGRNs || []).map((g: any) => String(g?.grnno ?? g?.grnNo ?? g?.grn ?? g ?? '')));

			forkJoin({
				grnDetails: this.purchasingService.getGrnDetails(this.vendor_data.supplierid),
				grnBatches: this.purchasingService.getOpenGrnBatches(this.vendor_data.supplierid)
			}).subscribe((results: any) => {
				this.all_open_grns = results.grnDetails || [];
				this.all_grn_batches = results.grnBatches || [];
				this.open_grns = this.all_open_grns;
				this.grn_batches = this.all_grn_batches;

				// A GRN line this action just created — by GRN# (most reliable), else the new PO#, else
				// (when the modal returns neither) any GRN not in the pool before we opened the modal.
				// This covers no-PO items added to a BRAND-NEW PO *and* to an EXISTING open PO.
				const isNew = (grn: any): boolean => {
					const no = String(grn.grnno);
					if (createdGrnNos.size && createdGrnNos.has(no)) { return true; }
					if (createdPoNos.size && createdPoNos.has(String(grn.orderno))) { return true; }
					if (!createdGrnNos.size && !createdPoNos.size) { return !priorGrnNos.has(no); }
					return false;
				};

				let added = 0;
				this.all_open_grns.forEach((grn: any) => {
					if (!isNew(grn)) { return; }
					// Make sure the line's batch chip is shown (don't re-add a chip already selected).
					const batch = this.all_grn_batches.find((b: any) =>
						parseFloat(b.grnbatch) == parseFloat(grn.grnbatch) && parseFloat(b.orderno) == parseFloat(grn.orderno));
					if (batch && !this.selected_grnbatches.some((s: any) =>
						parseFloat(s.grnbatch) == parseFloat(batch.grnbatch) && parseFloat(s.orderno) == parseFloat(batch.orderno))) {
						this.selected_grnbatches.push(batch);
						const idx = this.grn_batches.indexOf(batch);
						if (idx >= 0) { this.grn_batches.splice(idx, 1); }
					}
					// Add the GRN line itself (dedup), mirroring addGrnBatch's defaults.
					const alreadySelected = this.selected_grns.some((s: any) => parseFloat(s.grnno) == parseFloat(grn.grnno));
					if (!alreadySelected) {
						if (grn.toinvoice === undefined || grn.toinvoice === null) {
							grn.toinvoice = (parseFloat(grn.qtyrecd) || 0) - (parseFloat(grn.quantityinv) || 0);
						}
						grn.toinvoice = parseFloat(grn.toinvoice) || 0;
						grn.additionaldesc = '';
						this.snapshotPoUnitPrice(grn);
						this.selected_grns.push(grn);
						added++;
					}
				});

				this.getTotal();
				this.globalSearchService.showNotification(
					added > 0
						? `${added} no-PO line(s) added — existing lines kept`
						: 'No-PO items created — pick their GRN batch below to add them',
					'success', 'bottom', 'right');
			});
		});
	}

	private rateForCurrcode(code: string): number {
		const cur = (this.currencies || []).find((c: any) => c.currabrev === code);
		if (cur) {
			const r = parseFloat(cur.rate);
			if (!isNaN(r) && r > 0) { return r * 100; }
		}
		const vendorRate = parseFloat(this.vendor_data?.exrate);
		return (!isNaN(vendorRate) && vendorRate > 0) ? vendorRate : 1;
	}

	private getBaseCurrcode(): string {
		const base = (this.currencies || []).find((c: any) => {
			const r = parseFloat(c.rate);
			return !isNaN(r) && Math.abs(r - 1) < 0.0001;
		});
		return base?.currabrev || '';
	}

	private syncCurrencyToForm(): void {
		if (!this.invoiceForm || !this.currencies?.length) { return; }
		const code = this.vendor_data?.currcode || this.getBaseCurrcode();
		if (!code) { return; }
		this.invoiceForm.get('currcode')?.setValue(code, { emitEvent: false });
		const rateControl = this.invoiceForm.get('rate');
		if (rateControl && !rateControl.dirty) {
			rateControl.setValue(this.rateForCurrcode(code), { emitEvent: false });
		}
	}

	ngAfterViewInit() {
		this.globalsService.getGLAccounts().subscribe((result: any) => {
			this.glaccounts = result;
			this.allglaccounts = result;
		});

		this.globalsService.getTaxes().subscribe((result: any) => {
			this.taxes = result;
		});

		this.globalSearchService.currencies.subscribe((currencies: any) => {
			this.currencies = currencies || [];
			this.syncCurrencyToForm();
		});

		this.generalLedgerService.getTags().subscribe((tagsResponse: any) => {
			this.glTags = (tagsResponse && tagsResponse.tags) ? tagsResponse.tags : [];
		});

	}

	onEnterKeyDown(event: KeyboardEvent): void {
		event.preventDefault();
		if (this.grnInput && this.grnInput.nativeElement) {
			setTimeout(() => {
				this.grnInput.nativeElement.focus();
			}, 0);
		}
	}
}
